'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { Fragment, useState } from 'react';

import { TierBenefit } from '@flipster/apis/v1/vip/types';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import Num, { BigNumber } from '@flipster/utils/num';

import { useDisplayQuoteCurrencies } from '../../../shared/quote-currency/state';
import { GlassCard } from '../../shared/glass-card';
import { vipProgramPageBunja } from '../../state';

type TabType = 'fee-rate' | 'upgrade-criteria';

interface Props {
  css?: Styles;
}

export const FeeTableSection = ({ css: cssProps }: Props) => {
  const [tab, setTab] = useState<TabType>('fee-rate');
  const { vipTierBenefitsAtom } = useBunja(vipProgramPageBunja);
  const vipTierBenefits = useAtomValue(vipTierBenefitsAtom);

  if (!vipTierBenefits) return null;

  const { volumeDays, vipTiers } = vipTierBenefits;

  // Calculate base fees from tier 0
  const baseTier = vipTiers.find((tier) => tier.vipTier === 0);
  const baseMakerFee = baseTier ? Num(baseTier.pswapMakerFee) : Num(0);
  const baseTakerFee = baseTier ? Num(baseTier.pswapTakerFee) : Num(0);

  return (
    <section className={css(vstack.raw({ pb: { base: 40, tablet: 64 }, px: 16 }), cssProps)}>
      <div
        className={vstack({
          gap: 16,
          pt: { base: 40, tablet: 64 },
          pb: { base: 32, tablet: 64 },
          color: 'content.default.level-5',
          textAlign: 'center',
        })}
      >
        <p className={css({ textStyle: 'Title/S', color: 'rgba(255, 208, 132)' })}>
          <FormattedMessage id='VIP.r260303.Vip_fee_upgrade_criteria' />
        </p>
        <h2 className={css({ textStyle: 'Display/XL' })}>
          <FormattedMessage
            id='Vip.r260303.Save_more_with_higher_VIP_tiers'
            values={{
              vip: (chunks: React.ReactNode) => (
                <span
                  className={css({
                    background: 'linear-gradient(90deg, #FFD084 0%, #FFFCEE 100%)',
                    backgroundClip: 'text',
                    color: 'transparent',
                  })}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </h2>
      </div>
      <GlassCard
        css={{
          pb: { base: 24, tablet: 20 },
          rounded: { base: 16, tablet: 28 },
          w: '100%',
        }}
      >
        <div
          className={css({
            w: '100%',
            py: { base: 16, tablet: 24 },
            px: { tablet: 24 },
            mr: 24,
            overflowX: 'scroll',
            scrollbarWidth: 'none',
          })}
        >
          <div
            className={hstack({
              w: { tablet: '100%' },
              gap: { base: 8, tablet: 20 },
              px: { base: 16, tablet: 0 },
              tabletDown: {
                _after: {
                  content: '""',
                  flexShrink: 0,
                  w: 8,
                },
              },
            })}
          >
            <TabButton
              isSelected={tab === 'fee-rate'}
              onClick={() => setTab('fee-rate')}
              label={<FormattedMessage id='Vip.r260121.Fee_rate' />}
            />
            <TabButton
              isSelected={tab === 'upgrade-criteria'}
              onClick={() => setTab('upgrade-criteria')}
              label={<FormattedMessage id='Vip.r260121.Upgrade_criteria' />}
            />
          </div>
        </div>
        <div className={stack({ w: '100%', px: { base: 16, tablet: 24 }, mt: { tabletDown: 16 } })}>
          {tab === 'fee-rate' && (
            <FeeRateTable vipTiers={vipTiers} baseMakerFee={baseMakerFee} baseTakerFee={baseTakerFee} />
          )}
          {tab === 'upgrade-criteria' && <UpgradeCriteriaTable vipTiers={vipTiers} volumeDays={volumeDays} />}
        </div>
      </GlassCard>
    </section>
  );
};

interface FeeRateTableProps {
  vipTiers: TierBenefit[];
  baseMakerFee: BigNumber;
  baseTakerFee: BigNumber;
}

const FeeRateTable = ({ vipTiers, baseMakerFee, baseTakerFee }: FeeRateTableProps) => {
  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: { base: 'auto 1fr 1fr', tablet: '180px 1fr 1fr' },
        rowGap: 8,
      })}
    >
      <HeaderCell isFirst>
        <FormattedMessage id='Trade.r251212.Tier' />
      </HeaderCell>
      <HeaderCell>
        <FormattedMessage id='Vip.r260121.Maker_fee' />{' '}
        <span className={css({ textStyle: 'Text/S/Regular' })}>
          <FormattedMessage id='Vip.r260121.Futures_parentheses' />
        </span>
      </HeaderCell>
      <HeaderCell isLast>
        <FormattedMessage id='Vip.r260121.Taker_fee' />{' '}
        <span className={css({ textStyle: 'Text/S/Regular' })}>
          <FormattedMessage id='Vip.r260121.Spot_futures' />
        </span>
      </HeaderCell>
      {vipTiers.map((tier, index) => {
        const makerFee = Num(tier.pswapMakerFee);
        const takerFee = Num(tier.pswapTakerFee);
        const makerDiscount = baseMakerFee.gt(0)
          ? Math.round(Num(1).minus(makerFee.div(baseMakerFee)).times(100).toNumber())
          : undefined;
        const takerDiscount = baseTakerFee.gt(0)
          ? Math.round(Num(1).minus(takerFee.div(baseTakerFee)).times(100).toNumber())
          : undefined;

        return (
          <Fragment key={tier.vipTier}>
            <div
              className={hstack({
                py: { base: 12, tablet: 16 },
                pl: { tablet: 24 },
                pr: { base: 4, tablet: 0 },
                textStyle: 'Text/L/Strong',
                color: index > 3 ? 'rgba(255, 208, 132)' : 'content.default.level-5',
              })}
            >
              <FormattedMessage id='Autogen_common.r251126.s_VIP_tier' values={{ tier: tier.vipTier }} />
            </div>
            <FeeCell
              baseFee={baseMakerFee}
              fee={makerFee}
              discount={makerDiscount && makerDiscount > 0 ? makerDiscount : undefined}
            />
            <FeeCell
              baseFee={baseTakerFee}
              fee={takerFee}
              discount={takerDiscount && takerDiscount > 0 ? takerDiscount : undefined}
              isLast
            />
          </Fragment>
        );
      })}
    </div>
  );
};

interface FeeCellProps {
  baseFee: BigNumber;
  fee: BigNumber;
  discount?: number;
  isLast?: boolean;
}

const FeeCell = ({ baseFee, fee, discount, isLast }: FeeCellProps) => {
  const feePrecision = 3;
  const cellStyle = stack({
    py: { base: 12, tablet: 16 },
    pl: { base: 4, tablet: 0 },
    pr: isLast ? { tablet: 24 } : { base: 4, tablet: 0 },
    tabletDown: { flexDir: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 4 },
    tablet: { flexDir: 'row', justifyContent: 'flex-end', gap: 4 },
  });

  if (!discount) {
    return (
      <div className={cellStyle}>
        <Formatted.Percent
          isRate
          value={fee}
          precision={feePrecision}
          toFixed
          css={{
            textStyle: 'Text/L/Monospace/Regular',
            color: 'content.default.level-5',
          }}
        />
      </div>
    );
  }

  return (
    <div className={cellStyle}>
      <Formatted.Percent
        isRate
        value={baseFee}
        precision={feePrecision}
        toFixed
        css={{
          textStyle: 'Text/L/Monospace/Regular',
          color: 'content.default.level-1',
          textDecoration: 'line-through',
        }}
      />
      <div className={hstack({ gap: 4 })}>
        <ArrowRightIcon className={css({ w: 14, h: 14 })} color={token('colors.content.default.level-2')} />
        <Formatted.Percent
          isRate
          value={fee}
          precision={feePrecision}
          toFixed
          css={{
            textStyle: 'Text/L/Monospace/Regular',
            color: 'content.default.level-5',
          }}
        />
      </div>
      <Badge
        type='tint'
        color='neutral'
        css={{ color: 'rgba(255, 208, 132)', tabletDown: { mt: 4 }, tablet: { ml: 4 } }}
      >
        <FormattedMessage id='Vip.r260121.percent_off' values={{ percent: discount + '%' }} />
      </Badge>
    </div>
  );
};

interface UpgradeCriteriaTableProps {
  vipTiers: TierBenefit[];
  volumeDays: number;
}

const UpgradeCriteriaTable = ({ vipTiers, volumeDays }: UpgradeCriteriaTableProps) => {
  // 거래량 집계 대상 quote currency. 상장된 perpetual 에서 파생된다 (하드코딩 금지).
  const displayQuoteCurrencies = useDisplayQuoteCurrencies();

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: { base: 'minmax(48px, auto) 1fr 20px 1fr', tablet: '1fr 2fr 40px 2fr' },
        rowGap: 8,
      })}
    >
      <HeaderCell isFirst>
        <FormattedMessage id='Trade.r251212.Tier' />
      </HeaderCell>
      <HeaderCell>
        <FormattedMessage
          id='Vip.r260121.days_day_trading_volume_currency'
          values={{
            days: volumeDays,
            currency: (
              <span className={css({ textStyle: 'Text/S/Regular' })}>({displayQuoteCurrencies.join(' & ')})</span>
            ),
          }}
        />
      </HeaderCell>
      <HeaderCell isCenter>
        <FormattedMessage id='Vip.r260121.or' />
      </HeaderCell>
      <HeaderCell isLast>
        <FormattedMessage
          id='Vip.r260121.hours_hour_avg_asset_balance_currency'
          values={{ hours: 24, currency: <span className={css({ textStyle: 'Text/S/Regular' })}>(USDT)</span> }}
        />
      </HeaderCell>
      {vipTiers.map((tier) => {
        const tradingVolume = tier.requiredTradingVolume ? parseFloat(tier.requiredTradingVolume) : undefined;
        const assetBalance = tier.minimumBalance ? parseFloat(tier.minimumBalance) : undefined;
        return (
          <Fragment key={tier.vipTier}>
            <div
              className={css({
                py: { base: 12, tablet: 16 },
                pl: { tablet: 24 },
                pr: { base: 4, tablet: 0 },
                textStyle: 'Text/L/Strong',
                color: 'rgba(255, 208, 132)',
              })}
            >
              <FormattedMessage id='Autogen_common.r251126.s_VIP_tier' values={{ tier: tier.vipTier }} />
            </div>
            <CriteriaCell value={tradingVolume} />
            <div
              className={center({
                py: { base: 12, tablet: 16 },
                px: { base: 4, tablet: 0 },
              })}
            >
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-1',
                })}
              >
                <FormattedMessage id='Vip.r260121.or' />
              </p>
            </div>
            <CriteriaCell value={assetBalance} isLast />
          </Fragment>
        );
      })}
    </div>
  );
};

interface HeaderCellProps {
  children: React.ReactNode;
  isFirst?: boolean;
  isCenter?: boolean;
  isLast?: boolean;
}

const HeaderCell = ({ children, isFirst, isCenter, isLast }: HeaderCellProps) => {
  const base = flex.raw({
    tabletDown: {
      flexDirection: 'column',
      justifyContent: 'center',
    },
    tablet: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: isCenter ? 'center' : isFirst ? 'flex-start' : 'flex-end',
      gap: 4,
    },
    pb: 12,
    borderColor: 'border.default.level-1',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    textStyle: 'Text/M/Regular',
    color: 'content.default.level-2',
  });
  if (isFirst) return <div className={css(base, { pr: { base: 4, tablet: 0 }, pl: { tablet: 24 } })}>{children}</div>;
  if (isCenter) return <div className={css(base, center.raw({ px: { base: 4, tablet: 0 } }))}>{children}</div>;
  return (
    <div
      className={css(base, {
        pl: { base: 4, tablet: 0 },
        pr: isLast ? { tablet: 24 } : { base: 4, tablet: 0 },
        textAlign: 'right',
      })}
    >
      {children}
    </div>
  );
};

interface CriteriaCellProps {
  value?: number;
  isLast?: boolean;
}

const CriteriaCell = ({ value, isLast }: CriteriaCellProps) => {
  return (
    <div
      className={css({
        py: { base: 12, tablet: 16 },
        pl: { base: 4, tablet: 0 },
        pr: isLast ? { tablet: 24 } : { base: 4, tablet: 0 },
        display: 'flex',
        justifyContent: 'flex-end',
      })}
    >
      <p
        className={css({
          textStyle: 'Text/L/Monospace/Regular',
          color: 'content.default.level-5',
        })}
      >
        {value != null ? `≥ ${value.toLocaleString()}` : '-'}
      </p>
    </div>
  );
};

interface TabButtonProps {
  isSelected: boolean;
  onClick: () => void;
  label: React.ReactNode;
  css?: Styles;
}

const TabButton = ({ isSelected, onClick, label, css: cssProps }: TabButtonProps) => (
  <button
    type='button'
    onClick={onClick}
    className={css(
      center.raw({
        gap: 8,
        p: { base: 16, tablet: 24 },
        border: '1px solid transparent',
        rounded: 16,
        flexShrink: 0,
        flex: { tablet: 1 },
      }),
      !isSelected && {
        _hover: {
          bg: 'rgba(255, 208, 132, 0.1)',
        },
      },
      isSelected && {
        border: '1px solid rgba(255, 255, 255, 0.12)',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.02) 100%)',
      },
      cssProps,
    )}
  >
    <p
      className={css({
        textStyle: 'Display/L',
        background: 'linear-gradient(90deg, #FFD084 0%, #FFFCEE 100%)',
        backgroundClip: 'text',
        color: 'transparent',
      })}
    >
      {label}
    </p>
  </button>
);
